import { useMemo } from "react";

import { useMarketTopology } from "@/hooks/useMarketIntelligence";
import { GAS_REGIONS, MARKET_HUBS, MARKET_ISOS } from "@/lib/market-intelligence/topology";
import { isModuleAuthorized } from "@/lib/module-registry";
import { usePermissions } from "@/lib/permissions";

import { searchMarket, type MarketSearchHit } from "./market-search";

const MI_ACCESS = { requiredPermission: "market_intelligence.access" } as const;

/**
 * Market Intelligence hits for the top-bar search (see `market-search.ts`).
 *
 * `enabled` is false when the user lacks module access — the gate every MI
 * route reads — and then there are no hits, so search reads as it did before
 * MI existed.
 *
 * The live topology is fetched only once a search is under way (`active`);
 * until it lands, the static registry answers, so the first keystroke already
 * has results.
 */
export function useMarketSearch(
  query: string,
  active: boolean,
): { hits: MarketSearchHit[]; enabled: boolean } {
  const permissions = usePermissions();
  const enabled = isModuleAuthorized(MI_ACCESS, permissions);
  const { data: topology } = useMarketTopology({ enabled: enabled && active });

  const hits = useMemo(() => {
    if (!enabled || !active) return [];
    return searchMarket(
      query,
      {
        hubs: topology?.hubs ?? MARKET_HUBS,
        isos: topology?.isos ?? MARKET_ISOS,
        gasRegions: topology?.gasRegions ?? GAS_REGIONS,
      },
      permissions,
    );
  }, [enabled, active, query, topology, permissions]);

  return { hits, enabled };
}
